import React, { useState, useEffect } from "react"
import PageMetaTags from "components/PageMetaTags"
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  Row,
  TabContent,
  TabPane,
  NavLink,
  Modal,
  Spinner,
  Input,
  Label,
} from "reactstrap"
import BreadcrumbTitle from "components/BreadcrumbTitle"
import {
  FormSection,
  formValueSelector,
  getFormValues,
  initialize,
  isDirty,
  reduxForm,
  resetSection,
} from "redux-form"
import { SaveButton } from "components/Buttons"
import { connect } from "react-redux"
import {
  addNewExperience,
  changeEventPublishField,
  getEventAccessCodes,
  getEventCategories,
  getEventDeepLinkCodes,
  getEventDeepLinkUrl,
  getEventInviteList,
  getInitialAccessTags,
  getSelectedEvent,
  getVendorConfig,
  resetSelectedEvent,
  updateExperience,
} from "store/actions"
import { FORM_NAMES, URL_PATHS } from "constants/index"
import BottomNavigation from "components/BottomNavigation"
import { withRouter, Link } from "react-router-dom"
import SetUpTab from "./setUpTab"
import MenuFeaturesTab from "./menuFeaturesTab"
import {
  mapInitialValues,
  mapNewExpValuesToApi,
  mapUpdatedExpValuesToApi,
} from "./utils"
import UnsavedAlertModal from "components/UnsavedAlertModal"
import PermissionsTab from "./permissionsTab"
import UserManagementTab from "./userManagementTab"
import { LoadingIndicator } from "components/LoadingIndicator"
import experienceFormValidation from "./experienceFormValidation"
import Hyperlinks from "./hyperlinks"
import ModalComponent from "components/Modals"
import { generatePath } from "react-router-dom/cjs/react-router-dom"

const ExperienceForm = ({
  step1FormValues,
  addNewExperience,
  vendorId,
  userId,
  showSavingModal,
  resetSelectedEvent,
  selectedEvent,
  isDirty,
  thumbnailIsDirty,
  bgImageIsDirty,
  updateExperience,
  match,
  getEvent,
  getEventInviteList,
  getEventAccessCodes,
  getInitialAccessTags,
  getEventCategories,
  resetSection,
  history,
  selectedEventCategories,
  initializeForm,
  formValues,
  changeEventPublishField,
  getEventDeepLinkUrl,
  eventDeepLinkUrl,
  getEventDeeplinkCodes,
  deeplinkCodesAreLoading,
  eventDeepLinkCodes,
  getVendorConfig,
  vendorConfig,
  publishLoading,
}) => {
  const TABS = {
    SETUP: 1,
    MENU_FEATURES: 2,
    PERMISSIONS: 3,
    USER_MANAGEMENT: 4,
    HYPERLINKS: 5,
  }

  const DEFAULT_BREADCRUMB_ITEMS = [
    { title: "Dashboard" },
    {
      title: "Experiences",
      link: "/experiences",
    },
    {
      title: !!selectedEvent ? "Edit Experience" : "Add Experience",
      type: "tab",
      clickHandler: () => {
        setActiveTab(TABS.SETUP)
      },
    },
  ]

  const [activeTab, setActiveTab] = useState(TABS.SETUP)
  const [warningModalIsOpen, setWarningModalIsOpen] = useState(false)
  const [status, setStatus] = useState(0)
  const [breadcrumbItems, setBreadcrumbItems] = useState(
    DEFAULT_BREADCRUMB_ITEMS
  )
  const [publishModalIsOpen, setPublishModalIsOpen] = useState(false)

  const setSubTabBreadcrumb = items => {
    setBreadcrumbItems([...DEFAULT_BREADCRUMB_ITEMS, ...items])
  }

  useEffect(() => {
    if (activeTab !== 1) {
      let currentTab = ""
      switch (activeTab) {
        case 2:
          currentTab = "Menu Features"
          break
        case 3:
          currentTab = "Permissions"
          break
        case 4:
          currentTab = "User Management"
          break
        case 5:
          currentTab = "Hyperlinks"
          break
        default:
          currentTab = ""
          break
      }
      setBreadcrumbItems([
        ...DEFAULT_BREADCRUMB_ITEMS,
        {
          title: currentTab,
        },
      ])
    }
  }, [activeTab])

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!!vendorId) {
      getVendorConfig({ id: vendorId })
    }
  }, [vendorId])

  useEffect(() => {
    if (history?.location?.state?.tab) {
      setActiveTab(history?.location?.state?.tab)
    }
    if (match?.params?.id) {
      setLoading(true)
      getEvent(match?.params?.id)
      getEventInviteList(match?.params?.vendor_id, match?.params?.id, {
        o: "name",
        od: "asc",
      })
      getEventAccessCodes({ event_id: match?.params?.id, type: 0 })
      getInitialAccessTags({ event_id: match?.params?.id })
      getEventCategories({ event_id: match?.params?.id })
    }
    return () => {
      resetSelectedEvent()
    }
  }, [])

  useEffect(() => {
    if (selectedEvent) {
      initializeForm(
        mapInitialValues(selectedEvent, selectedEventCategories, formValues)
      )
      setStatus(selectedEvent?.published ? 1 : 0)
      setLoading(false)
    }
  }, [selectedEvent, selectedEventCategories])

  useEffect(() => {
    if (!!vendorConfig && !!selectedEvent && !eventDeepLinkUrl) {
      if (
        vendorConfig?.nfc_hyperlinks ||
        vendorConfig?.communication_hyperlinks
      ) {
        getEventDeepLinkUrl({ id: selectedEvent?.id })
      }
      if (vendorConfig?.nfc_hyperlinks) {
        getEventDeeplinkCodes({
          event_id: selectedEvent?.id,
          type: 3,
          page: 1,
          per_page: 25,
          o: "created_at",
          od: "desc",
        })
      }
    }
  }, [vendorConfig, selectedEvent])

  const updateExperienceHandler = () => {
    updateExperience(
      mapUpdatedExpValuesToApi(
        selectedEvent,
        step1FormValues,
        bgImageIsDirty,
        thumbnailIsDirty,
        userId,
        vendorId
      )
    )
  }

  const createExperienceHandler = () => {
    addNewExperience(mapNewExpValuesToApi(step1FormValues, userId, vendorId))
  }

  const markAsActive = tab => {
    return activeTab === tab ? "active" : ""
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <PageMetaTags
          title={!!selectedEvent ? "Edit Experience" : "Add new experience"}
        />
        <Container fluid>
          <BreadcrumbTitle
            title={
              !!selectedEvent
                ? `Edit Experience: ${
                    selectedEvent?.name ? selectedEvent?.name : ""
                  }`
                : "Add new experience"
            }
            breadcrumbItems={breadcrumbItems}
          />
          <Row className="d-flex justify-content-end mb-2">
            <Col xs="auto">
              <SaveButton
                content="Back"
                color="light"
                type="button"
                onClick={() => {
                  history.goBack()
                }}
              />
            </Col>
            {!!selectedEvent && (
              <Col xs="auto">
                <Link
                  to={generatePath(URL_PATHS.EVENT_REPORTS, {
                    id: selectedEvent?.id,
                  })}
                >
                  <SaveButton
                    type="button"
                    color="primary"
                    onClick={() => {}}
                    fullWidth={true}
                    content="Manage Experience Responses"
                  />
                </Link>
              </Col>
            )}
          </Row>
          {loading ? (
            <LoadingIndicator />
          ) : (
            <React.Fragment>
              <Row>
                <Col>
                  <Card>
                    <CardBody>
                      {!!selectedEvent && (
                        <Row className="d-flex justify-content-end align-items-baseline">
                          <Col xs="auto">
                            <label className="font-size-16 ">Status:</label>
                          </Col>
                          <Col xs="auto">
                            <Label check>
                              Inactive{" "}
                              <Input
                                type="radio"
                                checked={!selectedEvent?.published}
                                onChange={event => {
                                  const { end_date, start_date, ...rest } =
                                    selectedEvent
                                  changeEventPublishField({
                                    data: {
                                      ...rest,
                                      send_published_notification: false,
                                      published: false,
                                    },
                                    type: "feature",
                                  })
                                  setStatus(event?.target?.value)
                                }}
                              />
                            </Label>
                          </Col>
                          <Col xs="auto">
                            <Label check>
                              Published{" "}
                              <Input
                                type="radio"
                                checked={selectedEvent?.published}
                                onChange={event => {
                                  setPublishModalIsOpen(true)
                                  setStatus(event?.target?.value)
                                }}
                              />
                            </Label>
                          </Col>
                        </Row>
                      )}
                      <Nav pills tabs>
                        <NavLink
                          className={markAsActive(TABS.SETUP)}
                          onClick={() => {
                            setActiveTab(TABS.SETUP)
                          }}
                        >
                          <span style={{ fontSize: "16px" }}>Set up</span>
                        </NavLink>
                        <NavLink
                          className={markAsActive(TABS.MENU_FEATURES)}
                          onClick={() => {
                            setActiveTab(TABS.MENU_FEATURES)
                          }}
                          disabled={!selectedEvent}
                        >
                          <span style={{ fontSize: "16px" }}>
                            Menu Features
                          </span>
                        </NavLink>
                        <NavLink
                          className={markAsActive(TABS.PERMISSIONS)}
                          onClick={() => {
                            if (isDirty) {
                              setWarningModalIsOpen(true)
                            }

                            setActiveTab(TABS.PERMISSIONS)
                          }}
                          disabled={!selectedEvent}
                        >
                          <span style={{ fontSize: "16px" }}>Permissions</span>
                        </NavLink>
                        <NavLink
                          className={markAsActive(TABS.USER_MANAGEMENT)}
                          onClick={() => {
                            if (isDirty) {
                              setWarningModalIsOpen(true)
                            }
                            setActiveTab(TABS.USER_MANAGEMENT)
                          }}
                          disabled={!selectedEvent}
                        >
                          <span style={{ fontSize: "16px" }}>
                            User Management
                          </span>
                        </NavLink>
                        {(!!vendorConfig?.communication_hyperlinks ||
                          !!vendorConfig?.nfc_hyperlinks) && (
                          <NavLink
                            className={markAsActive(TABS.HYPERLINKS)}
                            onClick={() => {
                              setActiveTab(TABS.HYPERLINKS)
                            }}
                            disabled={!selectedEvent}
                          >
                            <span style={{ fontSize: "16px" }}>Hyperlinks</span>
                          </NavLink>
                        )}
                      </Nav>
                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={TABS.SETUP}>
                          <FormSection name="setup">
                            <SetUpTab
                              handleNextTab={() =>
                                setActiveTab(TABS.MENU_FEATURES)
                              }
                              handleSave={updateExperienceHandler}
                              handleCreate={createExperienceHandler}
                              setSubTabBreadcrumb={setSubTabBreadcrumb}
                              tabIsActive={activeTab === TABS.SETUP}
                            />
                          </FormSection>
                        </TabPane>
                        <TabPane tabId={TABS.MENU_FEATURES}>
                          <FormSection name="menuFeatures">
                            <MenuFeaturesTab />
                          </FormSection>
                        </TabPane>
                        <TabPane tabId={TABS.PERMISSIONS}>
                          <FormSection name="permissions">
                            <PermissionsTab />
                          </FormSection>
                        </TabPane>
                        <TabPane tabId={TABS.USER_MANAGEMENT}>
                          <UserManagementTab />
                        </TabPane>
                        {(!!vendorConfig?.communication_hyperlinks ||
                          !!vendorConfig?.nfc_hyperlinks) && (
                          <TabPane tabId={TABS.HYPERLINKS}>
                            <Hyperlinks
                              event_id={selectedEvent?.id}
                              deeplinkUrl={eventDeepLinkUrl}
                              deeplinkCodes={eventDeepLinkCodes}
                              deeplinkCodesAreLoading={deeplinkCodesAreLoading}
                            />
                          </TabPane>
                        )}
                      </TabContent>
                      {activeTab !== TABS.SETUP && (
                        <BottomNavigation
                          handleNext={
                            activeTab !== TABS.HYPERLINKS
                              ? () => setActiveTab(activeTab + 1)
                              : null
                          }
                          handleBack={() => setActiveTab(activeTab - 1)}
                        />
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </React.Fragment>
          )}
        </Container>
      </div>
      <Modal isOpen={publishLoading} centered={true} size="sm">
        <h4 style={{ textAlign: "center" }}>Saving...</h4>
        <CardBody className={"p-2 my-1 text-center"}>
          <Spinner className="ms-2" color="primary" />
        </CardBody>
      </Modal>
      <ModalComponent
        open={publishModalIsOpen}
        onClose={() => setPublishModalIsOpen(false)}
      >
        <Row>
          <h5>
            Would you like to send a notification to your users that the
            experience has been published?
          </h5>
        </Row>
        <Row className="mt-3 d-flex justify-content-end">
          <Col xs="auto">
            <SaveButton
              type="button"
              content="No"
              onClick={() => {
                const { end_date, start_date, ...rest } = selectedEvent
                changeEventPublishField({
                  data: {
                    ...rest,
                    send_published_notification: false,
                    published: true,
                  },
                  type: "feature",
                })
                setPublishModalIsOpen(false)
              }}
            />
          </Col>
          <Col xs="auto">
            <SaveButton
              type="button"
              content="Yes"
              onClick={() => {
                const { end_date, start_date, ...rest } = selectedEvent
                changeEventPublishField({
                  data: {
                    ...rest,
                    send_published_notification: true,
                    published: true,
                  },
                  type: "feature",
                })
                setPublishModalIsOpen(false)
              }}
            />
          </Col>
        </Row>
      </ModalComponent>
      <Modal isOpen={showSavingModal} centered={true} size="sm">
        <h4 style={{ textAlign: "center" }}>Saving...</h4>
        <CardBody className={"p-2 my-1 text-center"}>
          <Spinner className="ms-2" color="primary" />
        </CardBody>
      </Modal>
      <UnsavedAlertModal
        isOpen={warningModalIsOpen}
        onClose={() => {
          setWarningModalIsOpen(false)
          resetSection("setup")
        }}
        onSave={() => {
          setWarningModalIsOpen(false)
          updateExperienceHandler()
        }}
      />
    </React.Fragment>
  )
}

const selector = formValueSelector(FORM_NAMES.EXPERIENCE)

const mapStateToProps = state => ({
  formValues: getFormValues(FORM_NAMES.EXPERIENCE)(state),
  step1FormValues: selector(state, "setup"),
  vendorId: state.Login.vendorId,
  userId: state.Login.authUser.id,
  showSavingModal: state.experiences.showSavingModal,
  selectedEvent: state.experiences.selectedEvent,
  isDirty: isDirty(FORM_NAMES.EXPERIENCE)(state, ["setup"]),
  thumbnailIsDirty: isDirty(FORM_NAMES.EXPERIENCE)(state, [
    "setup.thumbnailImage",
  ]),
  bgImageIsDirty: isDirty(FORM_NAMES.EXPERIENCE)(state, ["setup.bgImage"]),
  selectedEventCategories: state.experiences.selectedEventCategories,
  eventDeepLinkUrl: state.experiences.eventDeepLinkUrl,
  eventDeepLinkCodes: state.experiences.eventDeepLinkCodes,
  deeplinkCodesAreLoading: state.experiences.deeplinkCodesAreLoading,
  vendorConfig: state.clients.vendorConfig,
  publishLoading: state.experiences.publishLoading,
})

const mapDispatchToProps = dispatch => ({
  addNewExperience: values => dispatch(addNewExperience(values)),
  resetSelectedEvent: () => dispatch(resetSelectedEvent()),
  updateExperience: values => dispatch(updateExperience(values)),
  initializeForm: values => dispatch(initialize(FORM_NAMES.EXPERIENCE, values)),
  getEvent: values => dispatch(getSelectedEvent(values)),
  getEventInviteList: (vendorId, eventId, sortOptions) =>
    dispatch(getEventInviteList(vendorId, eventId, sortOptions)),
  getEventAccessCodes: values => dispatch(getEventAccessCodes(values)),
  getInitialAccessTags: values => dispatch(getInitialAccessTags(values)),
  getEventCategories: values => dispatch(getEventCategories(values)),
  resetSection: values => dispatch(resetSection(FORM_NAMES.EXPERIENCE, values)),
  changeEventPublishField: values => dispatch(changeEventPublishField(values)),
  getEventDeepLinkUrl: values => dispatch(getEventDeepLinkUrl(values)),
  getEventDeeplinkCodes: values => dispatch(getEventDeepLinkCodes(values)),
  getVendorConfig: values => dispatch(getVendorConfig(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: FORM_NAMES.EXPERIENCE,
    validate: experienceFormValidation,
    initialValues: {
      permissions: { users: [null], access_type: "3" },
      setup: {
        textColor: "#01dcf2",
        bgColor: "#000000",
        menuColor: "#11ee75",
        layout: "2",
        font: "Arial",
        bgImageType: "1",
        thumbnailImageType: "1",
        bgAnimation: "0",
      },
    },
  })(withRouter(ExperienceForm))
)
